import { formatDurationLabel } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { type Theme } from "@mui/material";
import { Text, type TextProps } from "@src/appV2/redesign/system/ui-components";

interface LateCancellationTextProps extends Omit<TextProps, "color"> {
  readonly lateCancellationPeriod: number;
  readonly lateCancellationFeeHours: number;
  readonly color?: (theme: Theme) => string | undefined;
}

export function LateCancellationText(props: LateCancellationTextProps) {
  const {
    lateCancellationPeriod,
    lateCancellationFeeHours,
    variant = "body2",
    color: propsColor,
    ...textProps
  } = props;

  const hasLateCancellationPayout = lateCancellationPeriod > 0 && lateCancellationFeeHours > 0;

  const labelFormatOptions = { keepOriginalUnit: true };
  const lateCancellationPeriodText = formatDurationLabel(
    lateCancellationPeriod,
    "hour",
    labelFormatOptions
  );
  const lateCancellationFeeHoursText = formatDurationLabel(
    lateCancellationFeeHours,
    "hour",
    labelFormatOptions
  );

  return (
    <Text
      variant={variant}
      color={(theme) => {
        if (isDefined(propsColor)) {
          return propsColor(theme);
        }

        return theme.palette.text.secondary;
      }}
      {...textProps}
    >
      {hasLateCancellationPayout && (
        <>
          If the workplace cancels the shift within <strong>{lateCancellationPeriodText}</strong> of
          the start time, you&apos;ll be paid <strong>{lateCancellationFeeHoursText}</strong> at the
          scheduled rate.
        </>
      )}

      {!hasLateCancellationPayout && (
        <>
          This workplace does not offer cancellation payouts if they cancel the shift before its
          start time.
        </>
      )}
    </Text>
  );
}
